import { CommonModule } from '@angular/common';
import { Component, OnDestroy, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { ToastrService } from 'ngx-toastr';
import { Subject, Observable, takeUntil, debounceTime, distinctUntilChanged, switchMap, finalize } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { PaginationResult } from 'src/app/models/common.response';
import { DeleteDialog } from 'src/app/pages/dialogs/delete.component';
import { BillChargeService } from './bill-charge.service';
import { BillChargeDetailComponent } from './bill-charge-detail/bill-charge-detail.component';
import { BillChargeType, BillChargeTypeLabels } from 'src/app/common/enums/bill-charge-type';
import { PaginationRequest } from 'src/app/models/common.request';
import { BillCharge } from './ibill-charge';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-bill-charge',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    ReactiveFormsModule,
    TranslateModule
  ],
  templateUrl: './bill-charge.component.html',
  styleUrl: './bill-charge.component.scss'
})
export class BillChargeComponent implements OnDestroy {
  displayedColumns: string[] = [
    // '#',
    'name',
    'categorytype',
    'charge',
    'Default',
    'action'
  ];
  dataSource = new MatTableDataSource<BillCharge>();
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);
  searchBillCharge: FormControl = new FormControl();

  private _unsubscribeAll: Subject<any> = new Subject<any>();
  pagination: PaginationResult;
  billChargecount: number = 0;
  billCharges$: Observable<BillCharge[]>;
  billChargedata: BillCharge[];
  isLoading: boolean = false;
  isLoadData: boolean;
  searchQuery: string;
  public disabledTypes = Object.values(BillChargeType).filter(value => typeof value === 'number');
  pageSizeOptions = PAGE_SIZE_OPTIONS;



  constructor(
    private _billChargeService: BillChargeService,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private translate: TranslateService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }


  ngOnInit(): void {
    this._billChargeService.pagination$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.billChargecount = this.pagination.rowCount;
        }
      });

    this._billChargeService.billCharges$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.billChargedata = data;
        this.dataSource = new MatTableDataSource(this.billChargedata); // Correct initialization
        this.dataSource.paginator = this.paginator;
      });
    this.isLoadData = false;
    this.searchBillCharge.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._billChargeService.getBillCharge({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || ''
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.billCharges;
        this.billChargecount = response.result.pagination.rowCount;
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }


  onAddBillCharge() {
    this.dialog.open(BillChargeDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshBillCharge();
      }
    });
  }

  onUpdateBillCharge(billCharge: BillCharge) {
    const isDisabled = this.isActionDisabled(billCharge.name);
    this.dialog.open(BillChargeDetailComponent, {
      width: '600px',
      height: 'auto',
      disableClose: true,
      data: {
        BillChargeDetails: billCharge,
        isEdit: true,
        isDisabled: isDisabled
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshBillCharge();
      }
    });
  }

  onDeleteBillCharge(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.BILL_CHARGE') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._billChargeService.deleteBillCharge(id).subscribe((response) => {
          if (response.isSuccess) {
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.BILL_CHARGE')));
            this.refreshBillCharge();
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));

          }
        })
      }
    });
  }

  refreshBillCharge() {
    const globalSearchValue = this.searchBillCharge?.value?.trim();
    const requestPayload: PaginationRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._billChargeService.getBillCharge(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const categories = response.result.billCharges;
          this.billChargedata = categories;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(categories);
          } else {
            this.dataSource.data = categories;
          }
          this.dataSource.paginator = this.paginator;
          this.billChargecount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

  isActionDisabled(billChargeTypeName: string): boolean {
    const enumValue = Object.entries(BillChargeTypeLabels)
      .find(([_, label]) => label === billChargeTypeName)?.[0];
    return enumValue ? this.disabledTypes.includes(+enumValue) : false;
  }
}
